import { GTP_CHART_MESH_CLASSES, GTP_PLOT_EXCLUDED_CLASSES } from '@/utils/gtpOptions';
import {
  getGtpItemsFromRow,
  readGtpClassScore,
  type GeneGtpItem,
  type GtpClassScore,
} from '@/utils/formatGtpList';

export type GtpHeatmapGeneRow = Record<string, unknown>;

export type GtpHeatmapCell = {
  x: string;
  y: number;
  geneId: string;
  fullClassName: string;
  pEnrich?: number;
  pDeplete?: number;
  stars?: number;
};

export type GtpHeatmapSeries = {
  id: string;
  data: GtpHeatmapCell[];
};

export type GtpHeatmapModel = {
  genes: Array<{ id: string; symbol: string }>;
  classNames: string[];
  series: GtpHeatmapSeries[];
  maxPositive: number;
  minNegative: number;
  classPeakTces: Record<string, number>;
};

export function cosineSimilarity(a: number[], b: number[]): number {
  let dot = 0;
  let normA = 0;
  let normB = 0;
  for (let i = 0; i < a.length; i += 1) {
    dot += a[i] * b[i];
    normA += a[i] * a[i];
    normB += b[i] * b[i];
  }
  const denom = Math.sqrt(normA) * Math.sqrt(normB);
  return denom === 0 ? 0 : dot / denom;
}

export function euclideanDistance(a: number[], b: number[]): number {
  let sum = 0;
  for (let i = 0; i < a.length; i += 1) {
    const delta = a[i] - b[i];
    sum += delta * delta;
  }
  return Math.sqrt(sum);
}

function averageLinkageDistance(left: number[], right: number[], vectors: number[][]): number {
  let sum = 0;
  let count = 0;
  left.forEach(leftIndex => {
    right.forEach(rightIndex => {
      sum += euclideanDistance(vectors[leftIndex], vectors[rightIndex]);
      count += 1;
    });
  });
  return count === 0 ? 0 : sum / count;
}

function mergeClusterOrders(left: number[], right: number[], vectors: number[][]): number[] {
  const leftStart = left[0];
  const leftEnd = left[left.length - 1];
  const rightStart = right[0];
  const rightEnd = right[right.length - 1];
  const options = [
    { order: [...left, ...right], distance: euclideanDistance(vectors[leftEnd], vectors[rightStart]) },
    {
      order: [...left, ...[...right].reverse()],
      distance: euclideanDistance(vectors[leftEnd], vectors[rightEnd]),
    },
    {
      order: [...[...left].reverse(), ...right],
      distance: euclideanDistance(vectors[leftStart], vectors[rightStart]),
    },
    {
      order: [...[...left].reverse(), ...[...right].reverse()],
      distance: euclideanDistance(vectors[leftStart], vectors[rightEnd]),
    },
  ];
  options.sort((a, b) => a.distance - b.distance || a.order[0] - b.order[0]);
  return options[0].order;
}

/** Agglomerative clustering (average linkage) with a simple optimal leaf order. */
export function hierarchicalLeafOrder(vectors: number[][]): number[] {
  const count = vectors.length;
  if (count <= 1) return vectors.map((_, index) => index);

  const clusters = vectors.map((_, index) => [index]);

  while (clusters.length > 1) {
    let bestLeft = 0;
    let bestRight = 1;
    let bestDistance = Number.POSITIVE_INFINITY;

    for (let i = 0; i < clusters.length; i += 1) {
      for (let j = i + 1; j < clusters.length; j += 1) {
        const distance = averageLinkageDistance(clusters[i], clusters[j], vectors);
        if (distance < bestDistance) {
          bestDistance = distance;
          bestLeft = i;
          bestRight = j;
        }
      }
    }

    const merged = mergeClusterOrders(clusters[bestLeft], clusters[bestRight], vectors);
    clusters.splice(bestRight, 1);
    clusters.splice(bestLeft, 1);
    clusters.push(merged);
  }

  return clusters[0];
}

function geneIdFromRow(row: GtpHeatmapGeneRow): string {
  const id = row.ncbiID ?? row.geneNcbiID ?? row.gene_ncbi_id ?? row.id;
  return id == null || id === '' ? '' : String(id);
}

function geneSymbolFromRow(row: GtpHeatmapGeneRow, fallbackId: string): string {
  const symbol = row.symbol ?? row.geneSymbol ?? row.name;
  const label = typeof symbol === 'string' ? symbol.trim() : '';
  return label || fallbackId || 'Unknown';
}

function uniqueGeneSymbol(symbol: string, id: string, used: Set<string>): string {
  if (!used.has(symbol)) {
    used.add(symbol);
    return symbol;
  }
  const withId = id ? `${symbol} (${id})` : symbol;
  let label = withId;
  let suffix = 2;
  while (used.has(label)) {
    label = `${withId} #${suffix}`;
    suffix += 1;
  }
  used.add(label);
  return label;
}

function collectClassNames(rows: GtpHeatmapGeneRow[]): string[] {
  const extras: string[] = [];
  const seenExtras = new Set<string>();

  rows.forEach(row => {
    getGtpItemsFromRow(row).forEach((item: GeneGtpItem) => {
      const name = item.mesh_class_name?.trim();
      if (!name) return;
      if ((GTP_PLOT_EXCLUDED_CLASSES as readonly string[]).includes(name)) return;
      if ((GTP_CHART_MESH_CLASSES as readonly string[]).includes(name)) return;
      if (seenExtras.has(name)) return;
      seenExtras.add(name);
      extras.push(name);
    });
  });

  const orderedClasses: string[] = [...GTP_CHART_MESH_CLASSES].reverse();
  return orderedClasses.concat(extras);
}

function orderRowsBySearchItems(
  rows: GtpHeatmapGeneRow[],
  searchItems: Array<{ id?: unknown }> | undefined,
): GtpHeatmapGeneRow[] {
  if (!searchItems?.length) return rows;

  const indexById = new Map(searchItems.map((item, index) => [String(item.id ?? ''), index]));
  return [...rows].sort((a, b) => {
    const aIndex = indexById.get(geneIdFromRow(a)) ?? Number.MAX_SAFE_INTEGER;
    const bIndex = indexById.get(geneIdFromRow(b)) ?? Number.MAX_SAFE_INTEGER;
    return aIndex - bIndex;
  });
}

export function buildGtpHeatmapData(
  rows: GtpHeatmapGeneRow[],
  searchItems?: Array<{ id?: unknown }>,
  hiddenClassNames: Iterable<string> = [],
): GtpHeatmapModel {
  const orderedRows = orderRowsBySearchItems(rows, searchItems);
  const usedSymbols = new Set<string>();
  const genes = orderedRows
    .map(row => {
      const id = geneIdFromRow(row);
      const rawSymbol = geneSymbolFromRow(row, id);
      if (!id && rawSymbol === 'Unknown') return null;
      return {
        id: id || rawSymbol,
        symbol: uniqueGeneSymbol(rawSymbol, id, usedSymbols),
        row,
      };
    })
    .filter((gene): gene is { id: string; symbol: string; row: GtpHeatmapGeneRow } => gene != null);

  const classNames = collectClassNames(genes.map(gene => gene.row));
  const hidden = new Set([...hiddenClassNames].map(name => name.trim()).filter(Boolean));
  const classesToPlot = classNames.filter(name => !hidden.has(name));

  const scoresByGeneAndClass = genes.map(gene => {
    const byClass = new Map<string, GtpClassScore>();
    getGtpItemsFromRow(gene.row).forEach((item: GeneGtpItem) => {
      const name = item.mesh_class_name?.trim();
      if (!name || (GTP_PLOT_EXCLUDED_CLASSES as readonly string[]).includes(name)) return;
      byClass.set(name, readGtpClassScore(item));
    });
    return byClass;
  });

  const classPeakTces: Record<string, number> = {};
  classNames.forEach(className => {
    let peak = 0;
    scoresByGeneAndClass.forEach(byClass => {
      const value = byClass.get(className)?.tces ?? 0;
      if (Math.abs(value) > Math.abs(peak)) peak = value;
    });
    classPeakTces[className] = peak;
  });

  const emptyModel = (): GtpHeatmapModel => {
    const { maxPositive, minNegative } = tcesExtrema(Object.values(classPeakTces));
    return {
      genes: genes.map(({ id, symbol }) => ({ id, symbol })),
      classNames,
      series: [],
      maxPositive,
      minNegative,
      classPeakTces,
    };
  };

  if (genes.length === 0 || classesToPlot.length === 0) {
    return emptyModel();
  }

  const matrix = genes.map((_, geneIndex) =>
    classesToPlot.map(className => scoresByGeneAndClass[geneIndex].get(className)?.tces ?? 0),
  );

  const geneOrder = hierarchicalLeafOrder(matrix);
  const classVectors = classesToPlot.map((_, classIndex) =>
    geneOrder.map(geneIndex => matrix[geneIndex][classIndex]),
  );
  const classOrder = hierarchicalLeafOrder(classVectors);

  const orderedGenes = geneOrder.map(index => genes[index]);
  const orderedClasses = classOrder.map(index => classesToPlot[index]);

  const visibleTces: number[] = [];
  const series: GtpHeatmapSeries[] = orderedClasses.map((className, classIndex) => ({
    id: className,
    data: orderedGenes.map((gene, genePosition) => {
      const score = scoresByGeneAndClass[geneOrder[genePosition]].get(className);
      const tces = score?.tces ?? 0;
      visibleTces.push(tces);
      return {
        x: gene.symbol,
        y: tces,
        geneId: gene.id,
        fullClassName: className,
        ...(score?.pEnrich != null ? { pEnrich: score.pEnrich } : {}),
        ...(score?.pDeplete != null ? { pDeplete: score.pDeplete } : {}),
        ...(score && score.stars > 0 ? { stars: score.stars } : {}),
      };
    }),
  }));

  const { maxPositive, minNegative } = tcesExtrema(visibleTces);

  return {
    genes: orderedGenes.map(({ id, symbol }) => ({ id, symbol })),
    classNames,
    series,
    maxPositive,
    minNegative,
    classPeakTces,
  };
}

function mixChannel(from: number, to: number, t: number): number {
  return Math.round(from + (to - from) * t);
}

function parseHex(color: string): [number, number, number] {
  const hex = color.replace('#', '');
  return [parseInt(hex.slice(0, 2), 16), parseInt(hex.slice(2, 4), 16), parseInt(hex.slice(4, 6), 16)];
}

function mixHex(from: string, to: string, t: number): string {
  const a = parseHex(from);
  const b = parseHex(to);
  const rgb = a.map((channel, index) => mixChannel(channel, b[index], t));
  return `#${rgb.map(channel => channel.toString(16).padStart(2, '0')).join('')}`;
}

const GTP_HEATMAP_NEGATIVE = '#2166ac';
const GTP_HEATMAP_NEUTRAL = '#f7f7f7';
const GTP_HEATMAP_POSITIVE = '#b2182b';

function tcesExtrema(values: Iterable<number>): { maxPositive: number; minNegative: number } {
  let maxPositive = 0;
  let minNegative = 0;
  for (const value of values) {
    if (!Number.isFinite(value)) continue;
    if (value > maxPositive) maxPositive = value;
    if (value < minNegative) minNegative = value;
  }
  return { maxPositive, minNegative };
}

export function tcesToHeatmapColor(value: number, maxPositive: number, minNegative: number): string {
  if (!Number.isFinite(value) || value === 0) return GTP_HEATMAP_NEUTRAL;
  if (value > 0) {
    if (maxPositive <= 0) return GTP_HEATMAP_NEUTRAL;
    const t = Math.max(0, Math.min(1, value / maxPositive));
    return mixHex(GTP_HEATMAP_NEUTRAL, GTP_HEATMAP_POSITIVE, t);
  }
  if (minNegative >= 0) return GTP_HEATMAP_NEUTRAL;
  const t = Math.max(0, Math.min(1, value / minNegative));
  return mixHex(GTP_HEATMAP_NEUTRAL, GTP_HEATMAP_NEGATIVE, t);
}
